// components/NotificationPanel.tsx
import React, { useState, useEffect } from "react";
import { Notification } from "@services/NotificationService";
import { useNavigate } from "react-router-dom";
import { NotificationPanelProps } from "./utils/interfaces";

const NotificationPanel: React.FC<NotificationPanelProps> = ({
  isOpen,
  onClose,
  notifications,
  setNotifications,
  markAsRead,
  markAllAsRead,
  hasMore,
  loadMoreNotifications,
}) => {
  const [activeTab, setActiveTab] = useState<"all" | "unread">("all");
  const [menuOpen, setMenuOpen] = useState<number | null>(null);
  const navigate = useNavigate();

  const handleNotificationClick = async (notification: Notification) => {
    if (notification.unread) {
      await markAsRead(notification.id);
    }

    if (
      notification.object_type === "project" ||
      notification.object_type === "file"
    ) {
      const baseUrl = `/project/${notification.project_id}`;
      const isFileNotification =
        notification.notification_type === "file_processing_complete" ||
        notification.notification_type === "anomaly_scan_complete";

      const url = isFileNotification ? `${baseUrl}?tab=Documents` : baseUrl;
      navigate(url);
      onClose();
    } else if (notification.object_type === "report") {
      navigate(
        `/report/${notification.object_id}/project/${notification.project_id}`
      );
      onClose();
    } else if (notification.object_type === "program") {
      navigate(`/program/${notification.object_id}`);
      onClose();
    } else {
      console.warn("Unknown object type:", notification.object_type);
    }
  };

  useEffect(() => {
    const closeMenu = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        !target.closest(".notification-menu") &&
        !target.closest(".menu-toggle")
      ) {
        setMenuOpen(null);
      }
    };
    document.addEventListener("mousedown", closeMenu);

    const closePanel = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".notification-panel")) {
        onClose();
      }
    };
    document.addEventListener("mousedown", closePanel);

    return () => {
      document.removeEventListener("mousedown", closeMenu);
      document.removeEventListener("mousedown", closePanel);
    };
  }, []);

  const filtered =
    activeTab === "unread"
      ? notifications.filter((n) => n.unread)
      : notifications;

  return (
    <div
      className={`fixed notification-panel top-20 right-0 h-[calc(100vh-5rem)] w-[350px] 2xl:w-[400px] bg-white shadow-xl border-l border-t border-gray-200 flex flex-col transform transition-transform duration-300 z-[51] ${
        isOpen ? "translate-x-0" : "translate-x-full"
      }`}
    >
      {/* Header */}
      <div className="p-4 border-b flex justify-between items-center">
        <h3 className="font-semibold text-gray-700 text-base 2xl:text-lg">
          Notifications
        </h3>
        <div className="flex items-center space-x-3">
          {activeTab === "unread" && filtered.length > 0 && (
            <button
              className="text-blue-600 text-sm hover:underline"
              onClick={markAllAsRead}
              type="button"
            >
              Mark all as read
            </button>
          )}
          <button
            className="text-gray-600 hover:text-black text-base 2xl:text-lg"
            onClick={onClose}
            type="button"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Tabs (All / Unread) */}
      <div className="flex px-4 py-2 border-b text-gray-500 text-xs 2xl:text-sm">
        <button
          className={`mr-4 ${
            activeTab === "all"
              ? "font-semibold text-black border-b-2 border-black pb-1"
              : "hover:text-gray-700"
          }`}
          onClick={() => setActiveTab("all")}
          type="button"
        >
          All
        </button>
        <button
          className={`mr-4 flex items-center ${
            activeTab === "unread"
              ? "font-semibold text-black border-b-2 border-black pb-1"
              : "hover:text-gray-700"
          }`}
          onClick={() => setActiveTab("unread")}
          type="button"
        >
          Unread
          {notifications.some((n) => n.unread) && (
            <span className="ml-2 bg-gray-300 text-xs px-2 py-0.5 rounded-full">
              {notifications.filter((n) => n.unread).length}
            </span>
          )}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-gray-500 px-4 text-center">
            <img src="/emptyinbox.svg" alt="Empty" className="w-20 h-20 mb-4" />
            <p className="text-xs 2xl:text-sm">You are all caught up.</p>
          </div>
        ) : (
          filtered.map((n, index) => (
            <div
              key={n.id + "" + index}
              className={`p-4 border-b flex justify-between items-start cursor-pointer relative ${
                n.unread ? "bg-gray-100" : "hover:bg-gray-50"
              }`}
              onClick={() => handleNotificationClick(n)}
            >
              <div className="flex-1">
                <p className="text-xs 2xl:text-sm">{n.message}</p>
                <p className="text-[0.65rem] 2xl:text-xs text-gray-400 mt-1">
                  {new Date(`${n.created_at}Z`).toLocaleString()}
                </p>
              </div>
              <button
                className="menu-toggle  text-gray-500 text-xs 2xl:text-sm hover:text-gray-700 px-2 hover:bg-[#efefef] rounded-full"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen(menuOpen === n.id ? null : n.id);
                }}
                type="button"
              >
                ⋮
              </button>

              {menuOpen === n.id && (
                <div className="notification-menu absolute right-0 mt-6 w-32 bg-white border rounded shadow text-xs 2xl:text-sm z-50">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      markAsRead(n.id);
                      setMenuOpen(null);
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100"
                    type="button"
                  >
                    Mark as read
                  </button>
                </div>
              )}
            </div>
          ))
        )}
        {activeTab === "all" && hasMore && (
          <div className="p-3 text-center">
            <button
              onClick={loadMoreNotifications}
              className="px-3 py-1 text-xs text-blue-600 border border-blue-600 rounded hover:bg-blue-50"
              type="button"
            >
              Load more
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationPanel;
